/* Machine wise — every machine in every department against its own
 * standard: what is being lost, where, and what kind of loss it is. */

import { useEffect, useRef, useState } from 'react'
import { MAIN_STYLE, Subline } from '../App'
import { type Basis, machines, stageNoun } from '../lib/api_machines'
import type { MachineSummary } from '../lib/api_machines'
import { useAsync } from '../lib/useAsync'
import { Empty, ErrorBox, KpiRow, Loading, Segmented } from '../components/ui'
import { monthName, num } from '../lib/format'
import { FindingsTab } from './machines/FindingsTab'
import { RegisterTab } from './machines/RegisterTab'
import type { Sort } from './machines/RegisterTab'
import { TrendsTab } from './machines/TrendsTab'
import { ShiftsTab } from './machines/ShiftsTab'
import { StandardsTab } from './machines/StandardsTab'
import { dayNum, unitName } from './machines/parts'
import type { Ctx } from './machines/parts'

type Tab = 'find' | 'mc' | 'trend' | 'shift' | 'std'

export default function Machines() {
  const [tab, setTab] = useState<Tab>('find')
  const [month, setMonth] = useState<string | undefined>(undefined)
  const [unit, setUnit] = useState<number | null>(null)
  const [stage, setStage] = useState<string | null>(null)
  const [basis, setBasis] = useState<Basis>('settings')
  const [sort, setSort] = useState<Sort>('name')
  // The machine whose row is open in the register.
  const [sel, setSel] = useState<string | null>(null)
  // Set when a machine is opened from another tab, so the register can
  // bring its row into view once it has been drawn.
  const reveal = useRef<string | null>(null)

  const { data, error, loading, reload } = useAsync(
    () => machines.summary({ month, unit, stage, basis }), [month, unit, stage, basis])

  // A stage that is not on file for the month just picked (the combers and
  // the lap former have no August) would leave the page empty with nothing
  // on it saying why, so the filter falls back to all stages.
  const stale = !!data && !data.empty && !!stage && !data.stages.includes(stage)
  useEffect(() => { if (stale) setStage(null) }, [stale])

  useEffect(() => {
    if (tab !== 'mc' || !reveal.current) return
    document.getElementById(`mc-${reveal.current}`)?.scrollIntoView({ block: 'start' })
    reveal.current = null
  }, [tab, sel])

  // Opening a machine always lands on the register with its row open;
  // clicking the open row there closes it again.
  const open = (code: string) => {
    if (tab === 'mc') {
      setSel(sel === code ? null : code)
    } else {
      reveal.current = code
      setSel(code)
      setTab('mc')
    }
  }

  const ready = data && !data.empty ? data : null

  return (
    <>
      <div className="no-print" style={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
        gap: 20, flexWrap: 'wrap', padding: '14px 32px',
        borderBottom: '1px solid var(--color-divider)',
      }}>
        <Segmented<Tab>
          value={tab}
          minWidth={148}
          onChange={setTab}
          options={[
            { value: 'find', label: 'Findings' },
            { value: 'mc', label: 'Machines' },
            { value: 'trend', label: 'Trends' },
            { value: 'shift', label: 'Shifts' },
            { value: 'std', label: 'Standards' },
          ]}
        />
        {ready ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
            <Filter label="Month">
              <select value={ready.month} style={{ minWidth: 150 }}
                onChange={(e) => { setMonth(e.target.value); setSel(null) }}>
                {ready.months.map((m) => (
                  <option key={m} value={m}>{monthName(m, true)}</option>
                ))}
              </select>
            </Filter>
            <Filter label="Unit">
              <select value={unit ?? ''} style={{ minWidth: 130 }}
                onChange={(e) => { setUnit(e.target.value ? Number(e.target.value) : null); setSel(null) }}>
                <option value="">Both units</option>
                {ready.units.map((u) => <option key={u} value={u}>{unitName(u)}</option>)}
              </select>
            </Filter>
            <Filter label="Stage">
              <select value={stage ?? ''} style={{ minWidth: 150 }}
                onChange={(e) => { setStage(e.target.value || null); setSel(null) }}>
                <option value="">All stages</option>
                {ready.stages.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Filter>
            {/* The yarn stages can be read against the SITRA norm instead of
                the sheet's settings; the other stages have no norm on file. */}
            <Filter label="Standard">
              <Segmented<Basis> value={basis} minWidth={88} onChange={(b) => { setBasis(b); setSel(null) }}
                options={[{ value: 'settings', label: 'Settings' }, { value: 'sitra', label: 'SITRA' }]} />
            </Filter>
          </div>
        ) : null}
      </div>

      {/* Dimmed rather than cleared while a filter change is in flight, so
          the page does not collapse and jump on every pick. */}
      <main style={{ ...MAIN_STYLE, opacity: loading && data ? 0.55 : 1, transition: 'opacity 120ms ease' }}>
        {error ? <ErrorBox error={error} onRetry={reload} /> : null}
        {loading && !data ? <Loading what="the machines" /> : null}
        {data?.empty ? (
          <Empty title="No machine data yet">
            Load the daily tracker workbook on the Production page's <b>Data files</b> tab
            and this fills in.
          </Empty>
        ) : null}
        {ready ? (
          <Board data={ready} tab={tab} sort={sort} setSort={setSort}
            sel={sel} open={open} close={() => setSel(null)} />
        ) : null}
      </main>
    </>
  )
}

function Filter({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span className="axis-title">{label}</span>
      {children}
    </label>
  )
}

function Board({ data, tab, sort, setSort, sel, open, close }: {
  data: MachineSummary
  tab: Tab
  sort: Sort
  setSort: (s: Sort) => void
  sel: string | null
  open: (code: string) => void
  close: () => void
}) {
  const t = data.totals
  const monLong = monthName(data.month, true)
  const c: Ctx = {
    data,
    bench: data.benchmark,
    benchLabel: `${num(data.benchmark)}%`,
    monShort: monthName(data.month).slice(0, 3),
    monLong,
    days: data.dates.map(dayNum),
    open,
  }
  const first = data.dates[0]
  const last = data.dates[data.dates.length - 1]
  const left = t.implausible + t.over + t.no_standard

  return (
    <>
      <Subline>
        {data.dates.length} operating day{data.dates.length === 1 ? '' : 's'}
        {' · '}{dayNum(first)}–{dayNum(last)} {monLong}
        {' · '}{data.plant.machines} machines across {data.plant.stages} stages
        {data.units.length > 1 ? ', both units' : data.units.length ? `, ${unitName(data.units[0])}` : ''}
        {data.basis === 'sitra'
          ? ` · ${stageNoun(data.norm_stages)} read against SITRA norms (count gps × spindles), the rest against the sheet's settings`
          : ''}
        {left
          ? ` · ${num(left)} of ${num(t.rows)} machine-shifts set aside for a standard that cannot be used`
          : ''}
      </Subline>

      {data.plant.machines === 0 ? (
        <Empty title={`Too little on file for ${monLong}`}>
          {data.dates.length} day{data.dates.length === 1 ? '' : 's'} of the month
          {' '}{data.dates.length === 1 ? 'is' : 'are'} on file across {data.plant.on_file} machines.
          A machine is read once it has {data.min_days} rated days, so this month fills in
          as the tracker does. Pick an earlier month above.
        </Empty>
      ) : t.machines === 0 ? (
        <Empty title="No machines in this selection">
          Nothing on file for {data.scope.stage ?? 'any stage'} in
          {' '}{data.scope.unit ? unitName(data.scope.unit) : 'either unit'} in {monLong}.
        </Empty>
      ) : (
        <>
          <Kpis c={c} />
          {tab === 'find' ? <FindingsTab c={c} /> : null}
          {tab === 'trend' ? <TrendsTab c={c} /> : null}
          {tab === 'shift' ? <ShiftsTab c={c} /> : null}
          {tab === 'std' ? <StandardsTab c={c} /> : null}
          {tab === 'mc' ? (
            <RegisterTab c={c} sort={sort} setSort={setSort} sel={sel} close={close} />
          ) : null}
        </>
      )}
    </>
  )
}

function Kpis({ c }: { c: Ctx }) {
  const { data } = c
  const t = data.totals
  const y = data.yarn
  return (
    <KpiRow items={[
      { label: 'Machines tracked', value: num(t.machines),
        note: `${data.dates.length} days × 3 shifts` },
      // Read at the last stage that weighs yarn, for the unit in view and
      // whatever stage the filter is on — it is the plant's answer, not the
      // selection's.
      { label: 'Yarn production efficiency',
        value: y.efficiency === null ? '–' : num(y.efficiency, 1),
        unit: y.efficiency === null ? undefined : '%',
        note: y.standard
          ? `${num(y.actual)} kg actual vs ${num(y.standard)} kg target · ${y.stage}`
          : `no ${y.stage} data` },
      { label: 'Yarn output lost', value: num(y.lost / 1000, y.lost >= 100000 ? 0 : 1), unit: 't',
        note: `${num(y.lost)} kg yarn below standard` },
      { label: `Machine-days below ${c.benchLabel}`,
        value: t.machine_days ? num((t.below / t.machine_days) * 100) : '–',
        unit: t.machine_days ? '%' : undefined,
        note: `${num(t.below)} of ${num(t.machine_days)} machine-days` },
      { label: 'Machines to look at', value: num(t.flagged),
        note: 'carrying a named loss pattern' },
      { label: 'Stoppage time booked', value: num(Math.round(t.stop_min / 60)), unit: 'h',
        note: data.register
          ? `${num(t.stop_min)} minutes on the register`
          : 'no register on file for the month' },
    ]} />
  )
}
